/**
 * RenderEngine.js
 * ═══════════════════════════════════════════════════════════════
 * Abstraction layer for creative export inside the Advertising Lab.
 *
 * CURRENT BACKEND   : html2canvas  (browser-side, fully operational)
 * PREPARED BACKENDS : puppeteer    (server-side — API contract defined, not implemented)
 *                     remotion     (video-first  — stub only)
 *
 * Public API
 * ──────────
 *   prepareScene(iframeEl, { width, height })
 *     → freezes DOM, waits for fonts + images, pauses animations
 *     → returns cleanup() — call when done to restore state
 *
 *   renderToImage(iframeEl, { width, height, scale, format, quality, backend })
 *     → returns Promise<Blob>
 *
 *   renderToDataURL(iframeEl, options)
 *     → returns Promise<string>  (object URL — caller must revoke)
 *
 * Future Puppeteer contract
 * ──────────────────────────
 *   POST /api/render/image
 *   Body : { html: string, width: number, height: number, scale: number, format: 'png'|'webp' }
 *   Response : { url: string }   or raw binary with Content-Type header
 *
 * ═══════════════════════════════════════════════════════════════
 */

import html2canvas from 'html2canvas';
import { compileCreativeSchemaToHTML } from './CreativeSchema';

// ─── Backend constants ───────────────────────────────────────────────────────

export const RENDER_BACKENDS = Object.freeze({
  HTML2CANVAS : 'html2canvas',
  PUPPETEER   : 'puppeteer',  // server-side — not yet implemented
  REMOTION    : 'remotion',   // video-first  — not yet implemented
});

// ─── Internal helpers ────────────────────────────────────────────────────────

/**
 * Resolves once every <img> inside `doc` has loaded (or errored / timed out).
 */
async function _waitForImages(doc) {
  const imgs = Array.from(doc.images || []);
  await Promise.allSettled(
    imgs.map(img => {
      if (img.complete) return Promise.resolve();
      return new Promise(resolve => {
        img.onload  = resolve;
        img.onerror = resolve;
        setTimeout(resolve, 3000); // per-image hard cap
      });
    })
  );
}

/**
 * Resolves once `doc.fonts.ready` fires, with a hard timeout fallback.
 */
async function _waitForFonts(doc) {
  await Promise.race([
    doc.fonts?.ready ?? Promise.resolve(),
    new Promise(r => setTimeout(r, 4000)),
  ]);
}

// ─── Phase 1: DOM freeze / prepareScene ──────────────────────────────────────

/**
 * prepareScene
 * ────────────
 * Deterministically freezes the iframe before capture:
 *   1. Waits for all web fonts via document.fonts.ready
 *   2. Waits for all images to complete
 *   3. Locks body + html element dimensions precisely
 *   4. Injects a freeze <style> that pauses all CSS animations/transitions
 *   5. Forces a synchronous reflow so layout settles before html2canvas walks it
 *
 * Returns a cleanup function — call it after export to restore original styles.
 *
 * @param {HTMLIFrameElement} iframeEl
 * @param {{ width: number, height: number }} options
 * @returns {Promise<() => void>}
 */
export async function prepareScene(iframeEl, { width, height }) {
  const iframeDoc =
    iframeEl.contentDocument ||
    iframeEl.contentWindow?.document;

  if (!iframeDoc) {
    throw new Error('RenderEngine.prepareScene: iframe document not accessible');
  }

  // 1 + 2 — async waits in parallel
  await Promise.all([
    _waitForFonts(iframeDoc),
    _waitForImages(iframeDoc),
  ]);

  // 3 — snapshot existing inline styles for cleanup
  const prevBody = iframeDoc.body.getAttribute('style') || '';
  const prevHtml = iframeDoc.documentElement.getAttribute('style') || '';

  iframeDoc.body.style.cssText = [
    `width:${width}px`,
    `height:${height}px`,
    `overflow:hidden`,
    `margin:0`,
    `padding:0`,
  ].join('!important;') + '!important;';

  iframeDoc.documentElement.style.cssText = [
    `width:${width}px`,
    `height:${height}px`,
    `overflow:hidden`,
  ].join('!important;') + '!important;';

  // 4 — inject animation freeze
  const freezeTag = iframeDoc.createElement('style');
  freezeTag.id = '__re_freeze__';
  freezeTag.textContent =
    '*,*::before,*::after{animation-play-state:paused!important;transition:none!important;}';
  iframeDoc.head.appendChild(freezeTag);

  // 5 — force reflow
  void iframeDoc.body.offsetHeight;

  // Return cleanup
  return function cleanup() {
    iframeDoc.body.setAttribute('style', prevBody);
    iframeDoc.documentElement.setAttribute('style', prevHtml);
    iframeDoc.getElementById('__re_freeze__')?.remove();
  };
}

// ─── Phase 1: renderToImage ──────────────────────────────────────────────────

/**
 * renderToImage
 * ─────────────
 * Captures the iframe as an image Blob.
 *
 * @param {HTMLIFrameElement} iframeEl
 * @param {{
 *   width    : number,
 *   height   : number,
 *   scale    : number,        default 3  — 3× DPR for premium print-quality output
 *   format   : string,        default 'image/png'
 *   quality  : number,        default 1.0
 *   backend  : string,        default RENDER_BACKENDS.HTML2CANVAS
 * }} options
 * @returns {Promise<Blob>}
 */
export async function renderToImage(iframeEl, {
  width,
  height,
  scale   = 3,
  format  = 'image/png',
  quality = 1.0,
  backend = RENDER_BACKENDS.HTML2CANVAS,
} = {}) {
  if (backend === RENDER_BACKENDS.PUPPETEER) {
    // ── Future server-side path ─────────────────────────────────────────────
    // When implementing, POST to /api/render/image with the template HTML
    // and return the resulting Blob. The template HTML must be fully
    // self-contained (no relative asset URLs).
    //
    // Example:
    //   const res = await fetch('/api/render/image', {
    //     method : 'POST',
    //     headers: { 'Content-Type': 'application/json' },
    //     body   : JSON.stringify({ html, width, height, scale, format }),
    //   });
    //   return res.blob();
    // ───────────────────────────────────────────────────────────────────────
    throw new Error(
      'RenderEngine: Puppeteer backend requires /server/routes/render.js — not yet implemented.'
    );
  }

  if (backend === RENDER_BACKENDS.REMOTION) {
    throw new Error(
      'RenderEngine: Remotion backend is for video export — use handleDownloadVideo instead.'
    );
  }

  if (backend !== RENDER_BACKENDS.HTML2CANVAS) {
    throw new Error(`RenderEngine: unknown backend '${backend}'`);
  }

  return _html2canvasCapture(iframeEl, { width, height, scale, format, quality });
}

/**
 * renderToDataURL
 * ───────────────
 * Convenience wrapper — resolves to an object URL string.
 * IMPORTANT: caller must call URL.revokeObjectURL(url) when done.
 *
 * @param {HTMLIFrameElement} iframeEl
 * @param {object} options  — same as renderToImage
 * @returns {Promise<string>}
 */
export async function renderToDataURL(iframeEl, options = {}) {
  const blob = await renderToImage(iframeEl, options);
  return URL.createObjectURL(blob);
}

/**
 * renderToHTML
 * ────────────
 * Compiles a CreativeSchema into a self-contained HTML string.
 * This sits at the top of the rendering pipeline — between schema
 * construction and iframe injection.
 *
 * Pipeline position:
 *   CreativeSchema → renderToHTML → iframe srcDoc → renderToImage → Blob
 *
 * @param {object} schema  — CreativeSchema object
 * @returns {string}       — complete HTML document string
 */
export function renderToHTML(schema) {
  return compileCreativeSchemaToHTML(schema);
}

// ─── html2canvas implementation ──────────────────────────────────────────────

async function _html2canvasCapture(iframeEl, { width, height, scale, format, quality }) {
  const iframeDoc =
    iframeEl.contentDocument ||
    iframeEl.contentWindow?.document;

  if (!iframeDoc) {
    throw new Error('RenderEngine._html2canvasCapture: iframe document not accessible');
  }

  const canvas = await html2canvas(iframeDoc.body, {
    useCORS        : true,
    allowTaint     : true,
    scale,
    backgroundColor: '#000000',
    logging        : false,
    width,
    height,
    windowWidth    : width,
    windowHeight   : height,
    imageTimeout   : 12000,
    onclone: (clonedDoc) => {
      // Ensure the cloned document matches the target output dimensions exactly.
      // This prevents any scroll-offset or viewport-size mismatch between
      // the live preview iframe and the captured output.
      clonedDoc.body.style.cssText =
        `width:${width}px;height:${height}px;overflow:hidden;margin:0;padding:0;`;
      clonedDoc.documentElement.style.cssText =
        `width:${width}px;height:${height}px;overflow:hidden;`;
    },
  });

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob =>
        blob
          ? resolve(blob)
          : reject(new Error('RenderEngine: canvas.toBlob returned null')),
      format,
      quality,
    );
  });
}
